import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Trash2, Pencil } from "lucide-react";
import { ClassificationAdmin } from "@/pages/AdminServiceCodes";
import { ServiceApiKeys } from "@/components/ServiceApiKeys";

const TH = "px-3 py-2 text-left text-[11px] uppercase tracking-wider font-semibold text-muted-foreground";

function Zones({ zones, reload }) {
  const [name, setName] = useState("");
  const add = async () => {
    try {
      await api.post("/service/zones", { name, workshop_codes: [] });
      toast.success(`Zone "${name}" created`);
      setName("");
      reload();
    } catch (e) { toast.error(apiError(e)); }
  };
  const rename = async (z) => {
    const n = window.prompt("Rename zone", z.name);
    if (!n || n === z.name) return;
    try { await api.put(`/service/zones/${z.id}`, { name: n }); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (z) => {
    if (!window.confirm(`Delete zone ${z.name}? Its workshops become unassigned.`)) return;
    try { await api.delete(`/service/zones/${z.id}`); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="border bg-card rounded-md p-4 space-y-3" data-testid="svc-zones-panel">
      <h3 className="font-heading text-sm font-semibold">Service zones</h3>
      <div className="flex items-center gap-2">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. South Zone" className="h-9 w-56 text-sm" data-testid="svc-zone-name-input" />
        <Button onClick={add} disabled={!name.trim()} data-testid="svc-zone-add-btn">Add zone</Button>
      </div>
      <ul className="divide-y border rounded-md">
        {zones.map((z) => (
          <li key={z.id} className="flex items-center gap-3 px-3 py-2 text-sm" data-testid={`svc-zone-row-${z.name}`}>
            <b>{z.name}</b>
            <span className="text-xs text-muted-foreground">{z.workshop_codes.length ? z.workshop_codes.join(", ") : "no workshops yet"}</span>
            <button onClick={() => rename(z)} className="ml-auto text-muted-foreground hover:text-foreground" data-testid={`svc-zone-rename-${z.name}`}><Pencil size={13} /></button>
            <button onClick={() => remove(z)} className="text-muted-foreground hover:text-red-600" data-testid={`svc-zone-delete-${z.name}`}><Trash2 size={13} /></button>
          </li>
        ))}
        {zones.length === 0 && <li className="px-3 py-3 text-xs text-muted-foreground">No zones yet — assign workshops to a zone from the table below.</li>}
      </ul>
    </div>
  );
}

function Cutoff({ settings, reload }) {
  const [v, setV] = useState(settings?.cutoff || "11:00");
  useEffect(() => { if (settings?.cutoff) setV(settings.cutoff); }, [settings]);
  const save = async () => {
    try { await api.put("/service/settings", { cutoff: v }); toast.success("Cutoff saved"); reload(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="border bg-card rounded-md p-4 space-y-2" data-testid="svc-settings-panel">
      <h3 className="font-heading text-sm font-semibold">Upload cutoff</h3>
      <p className="text-xs text-muted-foreground">Daily DMS files received after this time (IST) are flagged late on the compliance board.</p>
      <div className="flex items-center gap-2">
        <input type="time" value={v} onChange={(e) => setV(e.target.value)} className="h-9 border rounded px-2 text-sm" data-testid="svc-cutoff-input" />
        <Button onClick={save} variant="outline" data-testid="svc-cutoff-save-btn">Save</Button>
      </div>
    </div>
  );
}

export function ServiceAdmin() {
  const [dealers, setDealers] = useState([]);
  const [zones, setZones] = useState([]);
  const [settings, setSettings] = useState(null);

  const load = useCallback(() => {
    api.get("/admin/dealers").then(({ data }) => setDealers(data)).catch((e) => toast.error(apiError(e)));
    api.get("/service/zones").then(({ data }) => setZones(data)).catch(() => {});
    api.get("/service/meta").then(({ data }) => setSettings(data.settings)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const zoneOf = (code) => zones.find((z) => z.workshop_codes.includes(code));

  const toggleWorkshop = async (d, on) => {
    try { await api.put(`/admin/dealers/${encodeURIComponent(d.code)}`, { has_workshop: on }); toast.success(`${d.code} ${on ? "is now a workshop" : "removed from workshops"}`); load(); } catch (e) { toast.error(apiError(e)); }
  };

  const assign = async (code, zoneId) => {
    try {
      const cur = zoneOf(code);
      if (cur && cur.id !== zoneId) await api.put(`/service/zones/${cur.id}`, { workshop_codes: cur.workshop_codes.filter((c) => c !== code) });
      if (zoneId !== "none") {
        const z = zones.find((x) => x.id === zoneId);
        await api.put(`/service/zones/${zoneId}`, { workshop_codes: [...z.workshop_codes, code] });
      }
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-4" data-testid="svc-admin">
      <ClassificationAdmin />
      <div className="grid gap-4 lg:grid-cols-2">
        <Zones zones={zones} reload={load} />
        <Cutoff settings={settings} reload={load} />
      </div>
      <ServiceApiKeys />
      <div className="border bg-card rounded-md overflow-auto">
        <div className="px-4 py-3 border-b"><h3 className="font-heading text-sm font-semibold">Workshops</h3><p className="text-xs text-muted-foreground">Dealers flagged as workshops appear in the Service Cockpit and are expected to upload the 4 DMS reports daily.</p></div>
        <table className="w-full text-sm">
          <thead className="border-b"><tr>{["Code", "Name", "District", "Facility", "Workshop", "Zone"].map((h) => <th key={h} className={TH}>{h}</th>)}</tr></thead>
          <tbody>
            {dealers.map((d) => (
              <tr key={d.code} className={`border-b last:border-0 hover:bg-muted/50 ${d.has_workshop ? "" : "text-muted-foreground"}`} data-testid={`svc-workshop-row-${d.code}`}>
                <td className="px-3 py-1.5 font-semibold">{d.code}</td>
                <td className="px-3 py-1.5">{d.name}</td>
                <td className="px-3 py-1.5 text-xs">{d.district}</td>
                <td className="px-3 py-1.5 text-xs">{d.facility}</td>
                <td className="px-3 py-1.5"><Switch checked={!!d.has_workshop} onCheckedChange={(v) => toggleWorkshop(d, v)} data-testid={`svc-workshop-toggle-${d.code}`} /></td>
                <td className="px-3 py-1.5">
                  {d.has_workshop && (
                    <Select value={zoneOf(d.code)?.id || "none"} onValueChange={(v) => assign(d.code, v)}>
                      <SelectTrigger className="h-7 w-44 text-xs" data-testid={`svc-workshop-zone-${d.code}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No zone</SelectItem>
                        {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
